<script setup lang="ts">
import {
    Backing,
    BackingGenerationState,
    BackingReportConfiguration, ConsumerBackingReport,
    ConsumerBackingReportType
} from "@/backing/interfaces/backing";
import GlowingContainer from "@/core/components/glowing-container/GlowingContainer.vue";
import BackingReportGenerateButton from "@/backing/components/BackingReportGenerateButton.vue";
import useConsumerBackingReports from "@/backing/composables/useConsumerBackingReports";
import GeneratedReportDialog from "@/generated-report/components/dialog/GeneratedReportDialog.vue";
import {ref, watch} from "vue";
import {GeneratedReport, ObjectSchema, ObjectValue} from "@/generated-report/interfaces";
import {backingApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";

const props = defineProps<{
    configuration: BackingReportConfiguration
    consumerKey: string;
    backing: Backing;
    type: ConsumerBackingReportType;
    disabled?: boolean;
    lastInstanceDate: Date | null;
}>();

const {showSuccess, showError} = useToastStore();

const {data, isValidating} = useConsumerBackingReports(props.consumerKey, props.backing.id, {
    limit: 1,
    type: props.type
});

const report = ref<ConsumerBackingReport | null>(null);
const readingReport = ref<boolean>(false);
const loading = ref<boolean>(false);


const modalConfig = ref<{
    open: boolean;
    state: BackingGenerationState;
    report: { data: GeneratedReport; schema: ObjectSchema } | null;
}>({open: false, state: 'idle' as BackingGenerationState, report: null});

watch(() => data.value, (newData) => {
    if (newData?.data?.length) {
        report.value = newData.data[0];
    } else {
        report.value = null;
    }
}, {immediate: true});

/**
 * Generate a new backing report.
 */
const generateReport = () => {
    modalConfig.value = {open: true, state: 'generating', report: null};
    backingApi().createConsumerReport(props.consumerKey, props.backing.id, {type: props.type})
        .then(async (result) => {
            report.value = result.data;
            modalConfig.value = {
                open: true,
                state: 'generated',
                report: {data: result.data, schema: result.schema},
            };
        })
        .catch(() => {
            modalConfig.value = {open: true, state: 'error', report: null};
        });
};

/**
 * Open an existing report in the dialog.
 * Read the report data if schema is not provided.
 */
const openExistingReport = (report: GeneratedReport, schema?: ObjectSchema) => {
    if (!schema) {
        readingReport.value = true;
        backingApi().readConsumerReport(props.consumerKey, props.backing.id, report.id)
            .then((result) => {
                modalConfig.value = {
                    open: true,
                    state: 'generated',
                    report: {data: result.data, schema: result.schema}
                };
            })
            .finally(() => {
                readingReport.value = false;
            });
    } else {
        modalConfig.value = {open: true, state: 'generated', report: {data: report, schema: schema}};
    }
};

/**
 * Update the report with new data.
 */
const updateReport = (data: ObjectValue, report: GeneratedReport) => {
    loading.value = true;
    backingApi().updateConsumerReport(props.backing.id, props.consumerKey, report.id, data)
        .then(async () => {
            showSuccess({content: 'Le rapport a bien été mis à jour.'});

            await backingApi().download(props.backing.id, props.consumerKey, report.id)
                .then(async (result) => {
                    const url = window.URL.createObjectURL(result);
                    window.open(url);
                });
        })
        .catch(() => {
            showError({content: 'Une erreur est survenue lors de la mise à jour du rapport.'});
        })
        .finally(() => {
            loading.value = false;
        });
};

/**
 * Handle the button click to either generate or open existing report.
 *
 * Generate a report if there is no report, the report has failed, or the last instance date is more recent than the report date.
 * Otherwise, open the existing report.
 */
const handleButtonClick = () => {
    if (report.value) {
        const reportDate = new Date(report.value.created_at);

        if (
            report.value.status === 'failed'
            || (props.lastInstanceDate && props.lastInstanceDate > reportDate)
        ) {
            return generateReport();
        } else {
            return openExistingReport(report.value);
        }
    }

    return generateReport();
};
</script>

<template>
  <GlowingContainer>
    <img
      v-if="type === 'patient'" src="@/assets/images/report-handoff.png" class="right-0 absolute w-32"
      alt="report-handoff"
    >
    <div
      class="p-4 space-y-2 z-10" :class="{
        'mr-32' : type === 'patient'
      }"
    >
      <h2 class="text-lg font-semibold">
        {{ configuration.title }}
      </h2>
      <p v-if="configuration.description" class="text-sm text-gray-600">
        {{ configuration.description }}
      </p>
      <BackingReportGenerateButton
        :loading="isValidating || readingReport || modalConfig.state === 'generating'"
        :action-label="configuration.action_button || null"
        :disabled="disabled || loading"
        @click="handleButtonClick"
      />
    </div>
    <GeneratedReportDialog
      v-model:open="modalConfig.open"
      :state="modalConfig.state"
      :report="modalConfig.report"
      :loading="loading"
      @save="(_data, _report) => updateReport(_data, _report)"
    />
  </GlowingContainer>
</template>
